import { ParentChildMappingPage } from "./Pages/ParentChildMappingPage";
import { App } from "./App";

/**
 * ## Parent Child
 *
 * Methods available when the pageMode is ParentChild.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // load mapping in the child part
 * DMS.ParentChild.loadChild('MappingName', 'editMode=add');
 *
 * // clear child content
 * DMS.ParentChild.clearChild();
 * ```
 */
export class ParentChild {
    private static getPage(): ParentChildMappingPage {
        return App.current.router.currentPage as ParentChildMappingPage;
    }

    /**
     * Load a mapping into the child container.
     * @param mappingName Name of the mapping to load.
     * @param params Additional parameters in querystring format.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // load mapping in the child part
     * DMS.ParentChild.loadChild('MappingName', 'editMode=add');
     * ```
     */
    public static loadChild(mappingName: string, params: string): Promise<void> {
        return this.getPage().loadChildParams(mappingName, params);
    }

    /**
     * Load a mapping into the child container with a json string parameter.
     * @param mappingName Name of the mapping to load.
     * @param params Additional parameters in json format. The json property names must be in quotes.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // load mapping in the child part
     * DMS.ParentChild.loadChildJson('MappingName', '{ "editMode": "add" }');
     * ```
     */
    public static loadChildJson(mappingName: string, params: string): Promise<void> {
        return this.getPage().loadChildJson(mappingName, params);
    }

    /**
     * Clear the content of the child container.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // clear child content
     * DMS.ParentChild.clearChild();
     * ```
     */
    public static clearChild(): void {
        this.getPage().clearChild();
    }

    /**
     * Reload the grids in the child container.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.ParentChild.reloadChild();
     * ```
     */
    public static reloadChild(): void {
        this.getPage().reloadChild();
    }

    /**
     * Returns true if the parent child mode is enabled and initialized.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.ParentChild.isPageModeParentChild(); // -> true;
     * ```
     */
    public static isPageModeParentChild(): boolean {
        return this.getPage() != null;
    }

    /**
     * Reload the grids in the parent container.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.ParentChild.reloadParent();
     * ```
     */
    public static reloadParent(): void {
        this.getPage().reloadParent();
    }

    /**
     * Reload the grids in the parent and child containers.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.ParentChild.reloadParentChild();
     * ```
     */
    public static reloadParentChild(): void {
        this.getPage().reloadParent();
        this.getPage().reloadChild();
    }

    /**
     * Load a page from given url into the child container.
     * @param url Url to be loaded.
     * @since 4.0.0
     */
    public static loadChildFromUrl(url: string): void {
        this.getPage().loadChildFromUrl(url);
    }
}
